// src/app/models/games/tiebreaker.models.ts

/**
 * Tiebreaker mode — replaces legacy TieBreaker enum for the new resolution system.
 * - none: split awards equally (no resolution)
 * - playoff: admin designates winner of physical playoff
 * - usga_cascade: automatic 7-step matching scorecards (server-side)
 * - manual: admin picks a hole subset for comparison
 */
export type TiebreakerMode = 'none' | 'playoff' | 'usga_cascade' | 'manual' | 'hardest_to_easiest' | 'alternating_hardest_easiest';

/**
 * Identifies which hole subset (or playoff) determined the winner.
 */
export type TiebreakerMethodKey = 'back9' | 'last6' | 'last3' | 'hole18' | 'front6' | 'front3' | 'hole9' | 'playoff';

/**
 * The resolution path — how the tiebreaker was triggered.
 */
export type TiebreakerPath = 'usga_cascade' | 'manual' | 'playoff';

/**
 * Controls which positions tiebreaker applies to.
 */
export type TiebreakerApplyTo = 'first_only' | 'all_positions';

/**
 * Defines a hole subset used for scorecard comparison.
 */
export interface HoleSubsetDef {
  key: TiebreakerMethodKey;
  label: string;
  holes: number[];       // 1-indexed hole numbers
  holeCount: number;
  fraction: number;      // holeCount / 18
}

/**
 * A persisted resolution record stored in GameCommonConfig.tiebreaker_resolutions.
 */
export interface TiebreakerResolution {
  boardKey: string;
  groupEventId: number;
  winnerId: number;
  path: TiebreakerPath;
  method: TiebreakerMethodKey;
  resolvedUtc: string;
}

/**
 * A single entry in a subset comparison result (used in manual mode).
 */
export interface SubsetComparisonEntry {
  playerId: number;
  playerName: string;
  grossSum: number | null;
  netSum: number | null;
  displaySum: number | null;
  isLowest: boolean;
  hasMissing: boolean;
}

/**
 * Result of comparing tied entries on a specific hole subset.
 */
export interface SubsetComparisonResult {
  subsetKey: TiebreakerMethodKey;
  entries: SubsetComparisonEntry[];
  winnerId: number | null;        // null if inconclusive
  isInconclusive: boolean;
}

/**
 * Tiebreaker configuration fields on each leaderboard's AwardsConfig.
 */
export interface TiebreakerConfig {
  tieBreakerMoney: TiebreakerMode;
  tieBreakerPoints: TiebreakerMode;
  tieBreakerApplyTo: TiebreakerApplyTo;
}

/**
 * The 7-step USGA cascade sequence for scorecard tiebreaker resolution.
 * Matches the backend HoleSubsets.CASCADE_SEQUENCE.
 */
export const HOLE_SUBSETS: HoleSubsetDef[] = [
  { key: 'back9',  label: 'Back 9 (Holes 10–18)', holes: [10, 11, 12, 13, 14, 15, 16, 17, 18], holeCount: 9, fraction: 0.5 },
  { key: 'last6',  label: 'Last 6 (Holes 13–18)', holes: [13, 14, 15, 16, 17, 18], holeCount: 6, fraction: 1 / 3 },
  { key: 'last3',  label: 'Last 3 (Holes 16–18)', holes: [16, 17, 18], holeCount: 3, fraction: 1 / 6 },
  { key: 'hole18', label: '18th Hole Only',        holes: [18], holeCount: 1, fraction: 1 / 18 },
  { key: 'front6', label: 'Front 6 (Holes 4–9)',  holes: [4, 5, 6, 7, 8, 9], holeCount: 6, fraction: 1 / 3 },
  { key: 'front3', label: 'Front 3 (Holes 7–9)',  holes: [7, 8, 9], holeCount: 3, fraction: 1 / 6 },
  { key: 'hole9',  label: '9th Hole Only',         holes: [9], holeCount: 1, fraction: 1 / 18 },
];
